import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Clock, 
  CheckCircle, 
  X, 
  AlertCircle,
  Search,
  User,
  Package,
  Calendar,
  Mail
} from 'lucide-react';
import { getProductRequests, ProductRequest, approveProductRequest, rejectProductRequest } from '../services/customerService';
import { getProducts, Product } from '../services/productService';
import { useToast } from './Toast';
import { logger } from '../utils/logger';

const RequestManagement: React.FC<{ view?: 'active' | 'approved' }> = ({ view }) => {
  const [productRequests, setProductRequests] = useState<ProductRequest[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { success, error: showError, ToastContainer } = useToast();
  
  // Helper to get product by ID or name
  const getProductForRequest = (request: ProductRequest): Product | null => {
    if (request.productId) {
      return allProducts.find(p => p.id === request.productId) || null;
    }
    if (request.productName) {
      return allProducts.find(p => p.name === request.productName) || null;
    }
    return null;
  };

  useEffect(() => {
    const loadRequests = async () => {
      try {
        setIsLoading(true);
        const [requests, products] = await Promise.all([
          getProductRequests(),
          getProducts()
        ]);
        setProductRequests(requests);
        setAllProducts(products);
        logger.log('Loaded product requests:', requests);
        logger.log('Loaded products:', products.length);
      } catch (error) {
        logger.error('Error loading product requests:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadRequests();
  }, []);

  const filteredRequests = productRequests.filter(request => {
    const matchesSearch = 
      request.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (request.productName && request.productName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (request.description && request.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Separate requests into Active and Past
  const activeRequests = filteredRequests.filter(request => 
    request.status === 'pending' || request.status === 'in-progress'
  );
  
  const pastRequests = filteredRequests.filter(request => 
    request.status === 'completed' || request.status === 'cancelled'
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in-progress':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'in-progress':
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'cancelled':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'normal':
        return 'bg-gray-100 text-gray-800';
      case 'low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      const result = await approveProductRequest(requestId);
      // Reload requests to update the UI
      const updatedRequests = await getProductRequests();
      setProductRequests(updatedRequests);
      success('Request Approved!', result.message);
    } catch (error) {
      logger.error('Error approving request:', error);
      showError('Failed to approve request', 'Please try again.');
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      await rejectProductRequest(requestId);
      // Reload requests to update the UI
      const updatedRequests = await getProductRequests();
      setProductRequests(updatedRequests);
      success('Request Rejected', 'The request has been cancelled.');
    } catch (error) {
      logger.error('Error rejecting request:', error);
      showError('Failed to reject request', 'Please try again.');
    }
  };

  const getRequestTypeIcon = (type: string) => {
    switch (type) {
      case 'product_request':
        return <Package className="h-4 w-4 text-gray-500" />;
      case 'installation':
        return <Bell className="h-4 w-4 text-gray-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
        <span className="ml-3 text-gray-600">Loading requests...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {view === 'approved' ? 'Approved devices' : 'Device requests'}
          </h2>
          <p className="text-gray-600">
            {view === 'approved'
              ? 'Completed device and installation requests'
              : 'Manage device and installation requests from customers'}
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {productRequests.length} total requests • {activeRequests.length} active • {pastRequests.length} past
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search requests by customer, product, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
            />
          </div>
          {!view && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          )}
        </div>
      </div>

      {view !== 'approved' && activeRequests.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          No active device requests.
        </div>
      )}

      {/* Active Requests */}
      {view !== 'approved' && activeRequests.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Clock className="h-5 w-5 text-yellow-600 mr-2" />
              Active Requests ({activeRequests.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">Requests that need your attention</p>
          </div>
          
          <div className="divide-y divide-gray-200">
            {activeRequests.map((request) => {
              const product = getProductForRequest(request);
              return (
              <div key={request.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 flex items-start space-x-4">
                    {/* Product Image */}
                    {product && (
                      <div className="flex-shrink-0">
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {product.image && product.image.startsWith('data:image/') ? (
                            <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                          ) : product.image && product.image.startsWith('/') ? (
                            <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                          ) : (
                            <Package className="h-10 w-10 text-gray-400" />
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getRequestTypeIcon(request.requestType)}
                      <h4 className="text-lg font-semibold text-gray-900">{request.productName || 'General Inquiry'}</h4>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        <span className="ml-1">{request.status}</span>
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getUrgencyColor(request.urgency)}`}>
                        {request.urgency} priority
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {request.customerName}
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-1" />
                        {request.customerEmail}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {request.createdAt.toLocaleDateString()}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{request.description}</p>
                    
                    <div className="text-xs text-gray-500">
                      Request Type: {request.requestType.replace('_', ' ')} • 
                      Created: {request.createdAt.toLocaleString()} • 
                      Updated: {request.updatedAt.toLocaleString()}
                    </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                    {request.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApproveRequest(request.id)}
                          className="flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleRejectRequest(request.id)}
                          className="flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Reject
                        </button>
                      </>
                    )}
                    {request.status === 'in-progress' && (
                      <span className="text-sm text-gray-600 font-medium">⏳ In Progress</span>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      )}

      {view === 'approved' && pastRequests.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          No approved device requests yet.
        </div>
      )}

      {/* Past Requests */}
      {view !== 'active' && pastRequests.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CheckCircle className="h-5 w-5 text-gray-600 mr-2" />
              Past Requests ({pastRequests.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">Completed and cancelled requests</p>
          </div>
          
          <div className="divide-y divide-gray-200">
            {pastRequests.map((request) => {
              const product = getProductForRequest(request);
              return (
              <div key={request.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 flex items-start space-x-4">
                    {/* Product Image */}
                    {product && (
                      <div className="flex-shrink-0">
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {product.image && product.image.startsWith('data:image/') ? (
                            <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                          ) : product.image && product.image.startsWith('/') ? (
                            <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                          ) : (
                            <Package className="h-10 w-10 text-gray-400" />
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getRequestTypeIcon(request.requestType)}
                      <h4 className="text-lg font-semibold text-gray-900">{request.productName || 'General Inquiry'}</h4>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        <span className="ml-1">{request.status}</span>
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getUrgencyColor(request.urgency)}`}>
                        {request.urgency} priority
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {request.customerName}
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-1" />
                        {request.customerEmail}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {request.createdAt.toLocaleDateString()}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{request.description}</p>
                    
                    <div className="text-xs text-gray-500">
                      Request Type: {request.requestType.replace('_', ' ')} • 
                      Created: {request.createdAt.toLocaleString()} • 
                      Updated: {request.updatedAt.toLocaleString()}
                    </div>
                    </div>
                  </div>
                  
                  {/* Status Display */}
                  <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                    {request.status === 'completed' && (
                      <span className="text-sm text-green-600 font-medium">✓ Completed</span>
                    )}
                    {request.status === 'cancelled' && (
                      <span className="text-sm text-red-600 font-medium">✗ Cancelled</span>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      )}

      {/* No Requests Found */}
      {filteredRequests.length === 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="text-center py-12">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filter criteria' : 'No requests have been submitted yet'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestManagement;
